import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Cell, Line, LineChart, Pie, PieChart, XAxis, YAxis } from "recharts";
import { IconAlertTriangle } from "@tabler/icons-react";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { MetricPanel, type Metric } from "@/components/dashboard/metric-cell";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { EmptyState } from "@/components/ui/empty-state";
import {
  fetchPerformanceCampaigns,
  fetchPerformanceSummary,
  type CurrencyView,
  type DailyPoint,
  type MetaDerived,
  type ObjectView,
  type RoasBucket,
  type RuleEventView,
  type SummaryView,
} from "@/services/metaPerformance.service";
import { cn } from "@/lib/utils";

import type { PerfScope } from "./AdPerformancePage";
import { BUCKET, METRIC_LABEL, fmtCross, fmtDelta, fmtInt, fmtMetricValue, fmtMoney, fmtPct, fmtRoas, shortDay } from "./format";
import { EventOutcome, MixedMoney } from "./bits";

const SERIES_COLORS = { spend: "var(--chart-1)", purchaseValue: "var(--chart-3)" };

/** Spend, returns and the Reveshare secondary row for the whole account. */
export function OverviewTab({ scope, onCurrency }: { scope: PerfScope; onCurrency: (c: CurrencyView) => void }) {
  const summary = useQuery({
    queryKey: ["meta-performance", "summary", scope.days, scope.reveshareOnly],
    queryFn: () => fetchPerformanceSummary(scope.days, scope.reveshareOnly),
  });
  const campaigns = useQuery({
    queryKey: ["meta-performance", "campaigns", scope.days, scope.reveshareOnly],
    queryFn: () => fetchPerformanceCampaigns(scope.days, scope.reveshareOnly),
  });
  useEffect(() => {
    if (summary.data) onCurrency(summary.data.currency);
  }, [summary.data, onCurrency]);

  const s = summary.data;
  const cur = s?.currency.account ?? "";
  const money = (v: number) => fmtMoney(v, cur);
  const series = (key: keyof DailyPoint) =>
    Object.fromEntries((s?.series ?? []).map((p) => [p.date, Number(p[key]) || 0]));
  const d = (key: keyof MetaDerived) => (s ? fmtDelta(s.delta[key], scope.days) : undefined);

  const headline: Metric[] = [
    { id: "perf-spend", label: "Ad spend", value: money(s?.current.spend ?? 0), change: d("spend"), series: series("spend"), format: money, unit: "spend" },
    { id: "perf-revenue", label: "Revenue (Meta)", value: money(s?.current.purchaseValue ?? 0), change: d("purchaseValue"), series: series("purchaseValue"), format: money, unit: "revenue" },
    { id: "perf-roas", label: "ROAS", value: fmtRoas(s?.current.roas), change: d("roas") },
    { id: "perf-purchases", label: "Purchases", value: fmtInt(s?.current.purchases ?? 0), change: d("purchases"), series: series("purchases"), unit: "purchase" },
  ];
  const secondary: Metric[] = [
    { id: "perf-cpa", label: "Cost per purchase", value: s?.current.cpa != null ? money(s.current.cpa) : "—", change: d("cpa") },
    { id: "perf-ctr", label: "CTR", value: fmtPct(s?.current.ctr), change: d("ctr") },
    { id: "perf-cpc", label: "CPC", value: s?.current.cpc != null ? money(s.current.cpc) : "—", change: d("cpc") },
    { id: "perf-cpm", label: "CPM", value: s?.current.cpm != null ? money(s.current.cpm) : "—", change: d("cpm") },
    { id: "perf-impressions", label: "Impressions", value: fmtInt(s?.current.impressions ?? 0), change: d("impressions"), series: series("impressions"), unit: "impression" },
    { id: "perf-reach", label: "Reach", value: fmtInt(s?.current.reach ?? 0), change: d("reach") },
    { id: "perf-frequency", label: "Frequency", value: s?.current.frequency != null ? s.current.frequency.toFixed(2) : "—", change: d("frequency") },
    { id: "perf-clicks", label: "Link clicks", value: fmtInt(s?.current.linkClicks ?? 0), change: d("linkClicks"), series: series("clicks"), unit: "click" },
  ];

  return (
    <div className="space-y-4">
      <MetricPanel id="perf-headline" title="This period" metrics={headline} columns="lg:grid-cols-4" loading={summary.isLoading} />
      <MetricPanel id="perf-secondary" title="Efficiency and reach" metrics={secondary} columns="lg:grid-cols-4" loading={summary.isLoading} />

      <div className="grid gap-4 lg:grid-cols-2">
        <SpendByCampaign campaigns={campaigns.data?.campaigns ?? []} loading={campaigns.isLoading} currency={cur} onPick={scope.openCampaign} />
        <SpendVsRevenue summary={s} loading={summary.isLoading} currency={cur} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ReveshareRow summary={s} loading={summary.isLoading} />
        <Attention events={s?.attention ?? []} loading={summary.isLoading} onOpenAd={scope.openAd} />
      </div>
    </div>
  );
}

/** Where the money went, coloured by how it came back. */
function SpendByCampaign({ campaigns, loading, currency, onPick }: { campaigns: ObjectView[]; loading: boolean; currency: string; onPick: (id: string) => void }) {
  const [active, setActive] = useState<number | null>(null);
  const total = campaigns.reduce((a, c) => a + c.meta.spend, 0);
  const data = useMemo(() => {
    const spent = campaigns.filter((c) => c.meta.spend > 0).sort((a, b) => b.meta.spend - a.meta.spend);
    const top = spent.slice(0, 7);
    const rest = spent.slice(7);
    const rows = top.map((c) => ({ id: c.id, name: c.name, value: c.meta.spend, roas: c.meta.roas, revenue: c.meta.purchaseValue, bucket: c.bucket }));
    if (rest.length) {
      const spend = rest.reduce((a, c) => a + c.meta.spend, 0);
      const value = rest.reduce((a, c) => a + c.meta.purchaseValue, 0);
      rows.push({ id: "", name: `Other (${rest.length})`, value: spend, roas: spend > 0 ? value / spend : null, revenue: value, bucket: "UNRATED" as RoasBucket });
    }
    return rows;
  }, [campaigns]);
  const counts = (["SCALE", "OPTIMIZE", "CUT", "UNRATED"] as RoasBucket[]).map((b) => ({ b, n: campaigns.filter((c) => c.bucket === b && c.meta.spend > 0).length }));

  return (
    <DashboardCard title="Spend by campaign" bodyClassName="px-5 pb-5">
      {loading ? (
        <div className="h-64 animate-pulse rounded-md bg-muted" />
      ) : data.length === 0 ? (
        <EmptyState bordered={false} title="No spend in this period" body="Campaigns that delivered show up here, coloured by ROAS" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <ChartContainer config={{ value: { label: "Spend" } }} className="mx-auto aspect-square h-56 w-full max-w-[260px]">
            <PieChart>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideIndicator
                    formatter={(_v, _n, item) => {
                      const p = item.payload as (typeof data)[number];
                      return (
                        <div className="space-y-0.5 text-xs">
                          <div className="font-medium text-foreground">{p.name}</div>
                          <div className="tabular-nums text-muted-foreground">Spend {fmtMoney(p.value, currency)} · {total > 0 ? Math.round((p.value / total) * 100) : 0}% of total</div>
                          <div className="tabular-nums text-muted-foreground">Revenue {fmtMoney(p.revenue, currency)} · ROAS {fmtRoas(p.roas)}</div>
                        </div>
                      );
                    }}
                  />
                }
              />
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius="55%"
                outerRadius="90%"
                paddingAngle={data.length > 1 ? 2 : 0}
                stroke="hsl(var(--card))"
                strokeWidth={2}
                onMouseEnter={(_, i) => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onClick={(_, i) => data[i]?.id && onPick(data[i].id)}
                className="cursor-pointer"
              >
                {data.map((p, i) => (
                  <Cell key={p.id || "other"} fill={BUCKET[p.bucket].fill} opacity={active === null || active === i ? 1 : 0.5} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          <ul className="space-y-1.5 text-xs">
            {counts.map(({ b, n }) => (
              <li key={b} className="flex items-center gap-2">
                <span aria-hidden className={cn("size-2 rounded-full", BUCKET[b].dot)} />
                <span className="text-foreground">{BUCKET[b].label}</span>
                <span className="text-muted-foreground">· {n}</span>
              </li>
            ))}
            <li className="pt-1 text-[11px] text-muted-foreground">Thresholds come from each campaign's rule, or 1.0 / 1.9 without one</li>
          </ul>
        </div>
      )}
    </DashboardCard>
  );
}

/** Two money series on one axis: what went out and what Meta says came back. */
function SpendVsRevenue({ summary, loading, currency }: { summary: SummaryView | undefined; loading: boolean; currency: string }) {
  const data = (summary?.series ?? []).map((p) => ({ ...p, label: shortDay(p.date) }));
  const any = data.some((p) => p.spend > 0 || p.purchaseValue > 0);
  return (
    <DashboardCard title="Spend vs revenue, daily" bodyClassName="px-5 pb-5">
      {loading ? (
        <div className="h-64 animate-pulse rounded-md bg-muted" />
      ) : !any ? (
        <EmptyState bordered={false} title="Nothing delivered yet" body="Daily spend and Meta-reported revenue draw here once ads run" />
      ) : (
        <ChartContainer
          config={{ spend: { label: "Spend", color: SERIES_COLORS.spend }, purchaseValue: { label: "Revenue (Meta)", color: SERIES_COLORS.purchaseValue } }}
          className="h-64 w-full"
        >
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={52} tickFormatter={(v: number) => fmtMoney(v, currency, true)} />
            <ChartTooltip content={<ChartTooltipContent formatter={(v, name) => <span className="tabular-nums"><span className="text-muted-foreground">{name === "spend" ? "Spend" : "Revenue"}</span> {fmtMoney(Number(v) || 0, currency)}</span>} />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Line type="monotone" dataKey="spend" stroke="var(--color-spend)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="purchaseValue" stroke="var(--color-purchaseValue)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ChartContainer>
      )}
    </DashboardCard>
  );
}

/** Reveshare's own attribution beside Meta's: orders, revenue, commission, net ROAS. */
function ReveshareRow({ summary, loading }: { summary: SummaryView | undefined; loading: boolean }) {
  const r = summary?.reveshare;
  return (
    <DashboardCard title="Attributed on Reveshare" bodyClassName="px-5 pb-5">
      {loading || !summary ? (
        <div className="h-24 animate-pulse rounded-md bg-muted" />
      ) : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            Orders the storefront pixel tied to a Meta ad. Secondary to Meta's figures above; these are the sales commission and payouts are based on.
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Fact label="Orders" value={fmtInt(r?.orders ?? 0)} />
            <Fact label="Revenue" value={<MixedMoney m={r!.revenue} />} />
            <Fact label="Commission owed" value={<MixedMoney m={r!.commission} />} />
            <Fact label="Net ROAS" value={fmtCross(r?.netRoas)} hint={r?.netRoas.reason === "CURRENCY_CONFLICT" ? "Sales and ads are in different currencies, so this is not computed" : "Reveshare revenue over Meta spend"} />
          </dl>
          {summary.launchedAds > 0 && (
            <p className="mt-3 text-[11px] text-muted-foreground">{summary.launchedAds} ad{summary.launchedAds === 1 ? "" : "s"} launched from Reveshare on this account</p>
          )}
        </>
      )}
    </DashboardCard>
  );
}

function Fact({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="min-w-0" title={hint}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="truncate text-lg font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

/** Open rule events, newest first. */
function Attention({ events, loading, onOpenAd }: { events: RuleEventView[]; loading: boolean; onOpenAd: (id: string) => void }) {
  return (
    <DashboardCard title="Needs attention" to="/brand/meta-ads/performance?tab=rules" bodyClassName="px-5 pb-4">
      {loading ? (
        <div className="h-24 animate-pulse rounded-md bg-muted" />
      ) : events.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">No open rule breaches. Set ROAS and CTR guard rails per campaign on the Rules tab.</p>
      ) : (
        <ul className="divide-y divide-border">
          {events.map((e) => (
            <li key={e.id} className="flex items-center gap-3 py-2.5">
              <IconAlertTriangle size={16} className={cn("shrink-0", e.outcome === "PAUSED" ? "text-red-500" : "text-amber-500")} aria-hidden />
              <button type="button" onClick={() => onOpenAd(e.metaAdId)} className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-medium text-foreground">{e.adName ?? e.metaAdId}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {METRIC_LABEL[e.metric]}: {fmtMetricValue(e.metric, e.observed)} vs {fmtMetricValue(e.metric, e.threshold)}
                </div>
              </button>
              <EventOutcome outcome={e.outcome} />
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

